/**
 * CenterStageSpotlight — Sunum modunda yuvarlak masanın TAM ORTASINDA
 * çalışan dramatic broadcast sahnesi.
 *
 * MİMARİ KARAR:
 *   "Ortada konuşmacı olmasın" doktrini (RoundtableStage / CenterMonogram
 *   yorumları) **sadece klasik mod** için geçerlidir. Sunum (present)
 *   modu, izleyici-merkezli "Agentic Theatre" deneyimidir; küçük halo +
 *   alt-sol BigSpeakerCompactSlot bilgisi kalabalık hissini ortadan
 *   kaldırmaya yetmez. Operatör doğrulaması (2026-05): "ortaya 4x boyda
 *   parlak avatar + yanına animasyonlu ekolayzer" → bu bileşen.
 *
 * GÖRSEL KATMANLAR (z-index ascending):
 *   L0  Persona-themed radial bloom halo (lip-amp ile nefes alır)
 *   L1  Conic-gradient outer ring (animated, persona-glow renkli)
 *   L2  FFT 16-bar radial visualizer (yalnız `isAudible` iken mount)
 *   L3  Avatar disc — `lip-pulse` + `lip-jaw` lipsync coupling
 *   L4  Role badge (sağ alt) + Live indicator (sol üst)
 *   L5  Ekolayzer çubukları (sağda yatay, persona-glow renkli)
 *   L6  Kimlik kartı (alt: ad + uzmanlık + Sıradaki butonu)
 *
 * BOYUT FORMÜLÜ:
 *   diameter = clamp(min(stageWidth * 0.32, baseAvatarSize * 3.6), 200, 460)
 *   - Sahne küçük olsa da en az 200 px (laptop projektör test edildi)
 *   - 460 px tavanı: UHD ekranda dahi merkez tablonun çapını yutmasın
 *   - 4x carpan operatör isteği; rim avatar 80-240 px arasında oynadığı
 *     için 3.6x katsayısı + tavan disiplini ile pratik 4x korunur
 *
 * SKIP API:
 *   `onSkip` prop'u — ServerTtsEngine'in window-level
 *   `__macdmServerTtsSkipCurrent` fonksiyonunu çağırır. Operator
 *   "hepsini dinlemeyeceğim" senaryosu için tek tıkla bir sonraki
 *   konuşmacıya geçer; kuyruk ve prefetch korunur.
 */
import { Suspense, lazy, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SkipForward } from "lucide-react";

import type { Persona } from "../../domain/types";
import { useLipsyncRef } from "../../hooks/useLipsync";
import { usePersonaTheme } from "../../hooks/usePersonaTheme";
import { useResolvedAvatarUrl } from "./AvatarAllocationContext";
import { RoleIcon, roleLabel } from "../common/RoleIcon";

const FFTRadialVisualizer = lazy(() =>
  import("../audio/FFTRadialVisualizer").then((m) => ({
    default: m.FFTRadialVisualizer,
  })),
);

interface CenterStageSpotlightProps {
  persona: Persona;
  baseAvatarSize: number;
  stageWidth: number;
  /**
   * Stage's RENDERED HEIGHT in CSS px. Boardroom topology is wider than
   * tall (`height ≈ width × boardroomAr ≈ width × 0.5`); without the
   * vertical clamp the spotlight + identity card overflow the table
   * boundary on the bottom seat row. Defaults to `stageWidth` for the
   * legacy circle topology where height == width.
   */
  stageHeight?: number;
  isAudible: boolean;
}

const SKIP_GLOBAL_KEY = "__macdmServerTtsSkipCurrent";

function dispatchSkipCurrent(): void {
  if (typeof window === "undefined") return;
  const w = window as Window & {
    [SKIP_GLOBAL_KEY]?: () => void;
  };
  w[SKIP_GLOBAL_KEY]?.();
}

export function CenterStageSpotlight({
  persona,
  baseAvatarSize,
  stageWidth,
  stageHeight,
  isAudible,
}: CenterStageSpotlightProps) {
  const themeStyle = usePersonaTheme(persona);
  const resolvedUrl = useResolvedAvatarUrl(persona);
  const lipRef = useLipsyncRef<HTMLDivElement>(isAudible);
  const pulseRef = useLipsyncRef<HTMLDivElement>(isAudible);

  const handleSkip = useCallback(() => {
    dispatchSkipCurrent();
  }, []);

  const initials = persona.displayName
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // Boyut hesabı — tek bir minor/short axis üzerinden:
  //   • Daire topolojisinde stageWidth == stageHeight → eski davranış aynı.
  //   • Boardroom topolojisinde stageHeight ≈ stageWidth × 0.5 → kısa
  //     ekseni baz aldığımız için identity card + ekolayzer alt seat
  //     sırasını ezmez, taşmaz, sıkışmaz.
  // Ek olarak yatay carde + ekolayzer için stageWidth'ten genişlik
  // budget'i çekiyoruz (avatar + gap + equalizer + identity card sığsın).
  const shortAxis = Math.min(stageWidth, stageHeight ?? stageWidth);
  const diameter = Math.round(
    Math.max(
      180,
      Math.min(
        460,
        Math.min(stageWidth * 0.32, shortAxis * 0.55, baseAvatarSize * 3.6),
      ),
    ),
  );
  const fftRingSize = Math.round(diameter * 1.18);
  const equalizerHeight = Math.round(diameter * 0.42);
  const equalizerWidth = Math.round(diameter * 0.22);
  const roleBadge = Math.round(diameter * 0.16);

  return (
    <div
      className="pointer-events-none absolute inset-0 z-[14] grid place-items-center"
      style={themeStyle as Record<string, string | number>}
      aria-label={`Merkez sahne: ${persona.displayName}`}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={persona.id}
          initial={{ opacity: 0, scale: 0.78, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.88, y: -8 }}
          transition={{ duration: 0.46, ease: [0.22, 1, 0.36, 1] }}
          className="pointer-events-auto relative flex items-center"
          style={{ gap: Math.round(diameter * 0.12) }}
        >
          {/* ═════════════════════════════════════════════════════════
           *  AVATAR KÜRESİ (4x boyda, parlak)
           * ═════════════════════════════════════════════════════════ */}
          <div className="relative flex flex-col items-center">
            {/* L0 — radyal halo (lip-amp ile nefes alır) */}
            <div
              ref={pulseRef}
              className="lip-halo pointer-events-none absolute rounded-full"
              style={{
                width: diameter * 1.55,
                height: diameter * 1.55,
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                background:
                  "radial-gradient(circle at center, hsl(var(--persona-glow) / 0.55) 0%, hsl(var(--persona-glow) / 0.18) 38%, transparent 72%)",
                animation: isAudible
                  ? undefined
                  : "idle-breathe 4.4s ease-in-out infinite",
                filter: "blur(2px)",
              }}
              aria-hidden
            />

            {/* L1 — animasyonlu konik ring */}
            <div
              className="pointer-events-none absolute rounded-full stage-conic-border opacity-80"
              style={{
                width: diameter + 18,
                height: diameter + 18,
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                ["--stage-border-speed" as string]: "11s",
              }}
              aria-hidden
            />

            {/* L2 — FFT radial visualizer */}
            {isAudible ? (
              <Suspense fallback={null}>
                <div
                  className="pointer-events-none absolute"
                  style={{
                    width: fftRingSize,
                    height: fftRingSize,
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <FFTRadialVisualizer
                    size={fftRingSize}
                    color="hsl(var(--persona-glow))"
                  />
                </div>
              </Suspense>
            ) : null}

            {/* L3 — Disc + lip-pulse */}
            <div
              className="lip-pulse relative grid place-items-center overflow-hidden rounded-full ring-[4px]"
              style={{
                width: diameter,
                height: diameter,
                background:
                  "linear-gradient(135deg, hsl(var(--persona-glow) / 0.55) 0%, hsl(var(--persona-glow) / 0.15) 100%)",
                borderColor: "hsl(var(--persona-glow) / 0.95)",
                ["--tw-ring-color" as string]: "hsl(var(--persona-glow) / 0.62)",
                boxShadow:
                  "0 28px 80px -20px rgba(0,0,0,0.95), inset 0 0 0 1px hsl(var(--persona-glow) / 0.35)",
              }}
            >
              {resolvedUrl ? (
                <div ref={lipRef} className="lip-jaw absolute inset-0">
                  <img
                    src={resolvedUrl}
                    alt={persona.displayName}
                    loading="eager"
                    draggable={false}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display =
                        "none";
                    }}
                  />
                </div>
              ) : (
                <div ref={lipRef} className="lip-jaw grid place-items-center">
                  <span
                    className="font-mono font-bold tracking-tight text-white drop-shadow-[0_8px_24px_rgba(0,0,0,0.85)]"
                    style={{ fontSize: Math.round(diameter * 0.32) }}
                  >
                    {initials}
                  </span>
                </div>
              )}

              {/* Alt iç gradient — kimlik kartı kontrastı için */}
              <div
                className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent"
                style={{ height: "32%" }}
                aria-hidden
              />

              {/* L4a — LIVE indicator */}
              {isAudible ? (
                <div
                  className="absolute z-10 flex items-center gap-1.5 rounded-full bg-black/75 backdrop-blur-md"
                  style={{
                    left: Math.round(diameter * 0.06),
                    top: Math.round(diameter * 0.06),
                    paddingLeft: Math.round(diameter * 0.04),
                    paddingRight: Math.round(diameter * 0.05),
                    paddingTop: Math.round(diameter * 0.012),
                    paddingBottom: Math.round(diameter * 0.012),
                  }}
                >
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-80" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.85)]" />
                  </span>
                  <span
                    className="font-bold uppercase tracking-[0.22em] text-rose-200"
                    style={{ fontSize: Math.max(9, Math.round(diameter * 0.04)) }}
                  >
                    Canlı
                  </span>
                </div>
              ) : null}

              {/* L4b — Rol rozeti (sağ alt) */}
              <span
                className="absolute z-10 grid place-items-center rounded-full bg-black ring-[3px] ring-black/85"
                style={{
                  width: roleBadge,
                  height: roleBadge,
                  right: -Math.round(roleBadge * 0.18),
                  bottom: -Math.round(roleBadge * 0.18),
                  borderColor: "hsl(var(--persona-glow))",
                }}
                title={roleLabel(persona.role)}
              >
                <RoleIcon
                  role={persona.role}
                  style={{
                    color: "hsl(var(--persona-glow))",
                    width: Math.round(roleBadge * 0.55),
                    height: Math.round(roleBadge * 0.55),
                  }}
                />
              </span>
            </div>

            {/* ═════════════════════════════════════════════════════════
             *  KİMLİK KARTI + SIRADAKİ BUTONU (alt)
             * ═════════════════════════════════════════════════════════ */}
            <div
              className="relative mt-6 flex flex-col items-center gap-3"
              style={{ maxWidth: diameter * 1.4 }}
            >
              <div
                className="rounded-2xl border border-white/[0.10] bg-black/75 backdrop-blur-2xl px-5 py-2.5 text-center shadow-[0_18px_44px_-14px_rgba(0,0,0,0.85)]"
                style={{
                  boxShadow:
                    "0 18px 44px -14px rgba(0,0,0,0.85), 0 0 0 1px hsl(var(--persona-glow) / 0.18)",
                }}
              >
                <div className="flex items-center justify-center gap-2">
                  <span
                    className="relative flex h-1.5 w-1.5"
                    aria-hidden
                  >
                    <span
                      className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-70"
                      style={{ background: "hsl(var(--persona-glow))" }}
                    />
                    <span
                      className="relative inline-flex h-1.5 w-1.5 rounded-full"
                      style={{
                        background: "hsl(var(--persona-glow))",
                        boxShadow:
                          "0 0 6px hsl(var(--persona-glow) / 0.85)",
                      }}
                    />
                  </span>
                  <span className="text-[9px] font-bold uppercase tracking-[0.26em] text-white/55">
                    Şu An Konuşan
                  </span>
                </div>
                <div
                  className="mt-1 truncate font-bold leading-tight text-white"
                  style={{ fontSize: Math.max(15, Math.round(diameter * 0.07)) }}
                >
                  {persona.displayName}
                </div>
                {persona.expertise ? (
                  <div className="mt-0.5 line-clamp-1 text-[11px] font-medium leading-snug text-white/55">
                    {persona.expertise}
                  </div>
                ) : null}
              </div>

              {/* SIRADAKİ — atla butonu */}
              <button
                type="button"
                onClick={handleSkip}
                className="focus-ring group relative inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/72 backdrop-blur-xl px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white/85 shadow-[0_10px_28px_-10px_rgba(0,0,0,0.75)] transition-all hover:scale-[1.04] hover:border-[hsl(var(--persona-glow)/0.65)] hover:bg-black/85 hover:text-white active:scale-[0.97]"
                title="Şu anki konuşmayı atla — sıradaki uzmana geç"
                aria-label="Sonraki konuşmacıya geç"
              >
                <span
                  className="pointer-events-none absolute inset-0 rounded-full opacity-0 transition-opacity group-hover:opacity-100"
                  style={{
                    background:
                      "radial-gradient(ellipse at center, hsl(var(--persona-glow) / 0.22), transparent 70%)",
                  }}
                  aria-hidden
                />
                <SkipForward
                  className="h-3.5 w-3.5"
                  strokeWidth={2.4}
                  aria-hidden
                />
                <span>Sıradaki</span>
              </button>
            </div>
          </div>

          {/* ═════════════════════════════════════════════════════════
           *  EKOLAYZER (sağda, dikey çubuklar — animasyonlu)
           *
           *  wave-organic-{1..5} keyframes (globals.css) — persona
           *  renginde currentColor; isAudible iken canlı, değilse
           *  yumuşak idle nefes. Animasyon CSS sadece 5 child için
           *  tanımlı (`:nth-child(1..5)`), bu yüzden çubuk sayısı 5
           *  sabit kalır.
           *
           *  Çubuk genişliği container'ın iç budget'ine TAM oturacak
           *  şekilde hesaplanır → eski "sola yaslanmış 5 çubuk + sağda
           *  37 px ölü alan" bug'ı kapatıldı (operatör geri bildirimi
           *  2026-05-e). `justify-content: center` defansif olarak
           *  korunur — gelecekte container budget değişirse çubuklar
           *  otomatik ortalanır. */}
          {(() => {
            const BAR_COUNT = 5;
            const BAR_GAP = 5;
            const barWidth = Math.max(
              4,
              Math.floor((equalizerWidth - BAR_GAP * (BAR_COUNT - 1)) / BAR_COUNT),
            );
            const usedWidth = barWidth * BAR_COUNT + BAR_GAP * (BAR_COUNT - 1);
            return (
              <div
                className="relative flex flex-col items-center gap-2 rounded-2xl border border-white/[0.08] bg-black/65 backdrop-blur-2xl px-3.5 py-3 shadow-[0_18px_44px_-14px_rgba(0,0,0,0.7)]"
                style={{
                  ["--persona-glow-css" as string]: "hsl(var(--persona-glow))",
                  boxShadow:
                    "0 18px 44px -14px rgba(0,0,0,0.7), 0 0 0 1px hsl(var(--persona-glow) / 0.20)",
                }}
                aria-hidden
              >
                <span
                  className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/55"
                  style={{ color: isAudible ? "hsl(var(--persona-glow))" : undefined }}
                >
                  Ses
                </span>
                <div
                  className="wave-organic"
                  style={{
                    color: "hsl(var(--persona-glow))",
                    height: equalizerHeight,
                    width: usedWidth,
                    opacity: isAudible ? 1 : 0.45,
                    gap: BAR_GAP,
                    justifyContent: "center",
                  }}
                >
                  {Array.from({ length: BAR_COUNT }, (_, idx) => (
                    <span
                      key={idx}
                      style={{
                        width: barWidth,
                        animationPlayState: isAudible ? "running" : "paused",
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
